import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Truck, CheckCircle, ArrowRight, Box, Zap, Shield, Star,
  Calculator, Globe, FileText, Cloud, Lock, Search,
  FolderOpen, CreditCard, ChevronDown, Quote, Package,
  BarChart3, Store, Layers, Warehouse
} from "lucide-react";

const LOGOS = {
  tdc_horizontal: "https://customer-assets-jt897jd0.emergentagent.net/job_todo-carton-refresh/artifacts/p7yq49g3_A13A1433-1615-4D2D-8764-DAF234BAAF1B.png",
  tdc_vertical: "https://customer-assets-jt897jd0.emergentagent.net/job_todo-carton-refresh/artifacts/1yggjjc0_IMG_3562.jpeg",
  tdc_dark: "https://customer-assets-jt897jd0.emergentagent.net/job_todo-carton-refresh/artifacts/djv40jun_B13344D7-2AA4-4B1D-AE94-ED9395AD1108.webp",
  tdc_vertical_big: "https://customer-assets-jt897jd0.emergentagent.net/job_todo-carton-refresh/artifacts/pw6tlv6d_FAAE2100-E5B4-408A-95E9-961BEE85339E.png",
  zipngo: "https://customer-assets-jt897jd0.emergentagent.net/job_todo-carton-refresh/artifacts/4hjy2fmk_30FFF72E-C679-49F2-BF56-21BE3B68074B.webp",
  archivo: "https://customer-assets.emergentagent.com/job_todo-carton-refresh/artifacts/1jhdh9ue_IMG_9519.jpeg",
};

const CARRIER_LOGOS = [
  { name: "DHL", color: "#FFCC00", bg: "#D40511" },
  { name: "FedEx", color: "#4D148C", bg: "#FF6600" },
  { name: "UPS", color: "#FFB500", bg: "#351C15" },
  { name: "Estafeta", color: "#fff", bg: "#E31937" },
  { name: "Redpack", color: "#fff", bg: "#E4002B" },
  { name: "Paquetexpress", color: "#fff", bg: "#00529B" },
];

const FAQ_DATA = [
  { q: "¿Cuánto tarda en llegar mi envío?", a: "Depende del servicio: Express 1-2 días, Standard 3-5 días, Económico 5-7 días. Todas las opciones incluyen rastreo en tiempo real." },
  { q: "¿Cómo funcionan las guías prepago?", a: "Compras un paquete de guías (5, 10, 25 o 50) con descuento por volumen. Las guías no vencen y puedes generar envíos con cualquier paquetería cuando quieras desde tu panel." },
  { q: "¿Qué incluye el servicio de Fulfillment?", a: "Almacenamiento en bodega, picking del producto, empaque con materiales de calidad (cajas, protección), etiquetado con guía de envío, y entrega al carrier. Todo automatizado." },
  { q: "¿Puedo conectar mi tienda de Mercado Libre o Amazon?", a: "Sí. Desde la sección Multicanal puedes conectar Mercado Libre y Amazon. Los pedidos se sincronizan automáticamente y se procesan con tu inventario." },
  { q: "¿Qué es el Archivo Muerto Digital?", a: "Digitalizamos, resguardamos y organizamos tus documentos históricos (contabilidad, RRHH, legal). Acceso 24/7 en la nube, cumplimiento SAT, y búsqueda inteligente con OCR." },
  { q: "¿Tienen API para integración?", a: "Sí. Ofrecemos una API REST completa con documentación para que conectes tu sistema ERP, tienda online o cualquier plataforma. Incluye webhooks para notificaciones en tiempo real." },
];

const TESTIMONIALS = [
  { name: "María González", role: "Directora de Operaciones", company: "Retail MX", text: "Redujimos nuestros costos de envío un 35% al migrar a ZipNGo. La comparación automática de tarifas nos ahorra horas de trabajo.", rating: 5 },
  { name: "Carlos Mendoza", role: "CEO", company: "E-commerce Plus", text: "El fulfillment de Todo de Cartón transformó nuestro negocio. Ya no nos preocupamos por empaque ni logística, ellos hacen todo.", rating: 5 },
  { name: "Ana Rodríguez", role: "Contadora", company: "Despacho ARG", text: "El servicio de Archivo Muerto Digital nos liberó una oficina completa. Ahora accedemos a todo desde la nube en segundos.", rating: 5 },
];

const HomePage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="grain-overlay">
      {/* HERO — Clean, Bold, Conversion-focused */}
      <section className="min-h-screen pt-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FAF9F6] via-white to-[#FFF8F0] -z-10" />
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-flex items-center gap-2 bg-[#5D4037]/10 text-[#5D4037] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <span className="w-2 h-2 bg-[#2E7D32] rounded-full animate-pulse" />
                + de 50 años de experiencia
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-[#5D4037] leading-[1.08]">
                Logística, fulfillment y <span className="text-[#F57C00]">e-commerce</span> bajo un mismo techo
              </h1>
              <p className="text-lg text-stone-600 mt-6 leading-relaxed max-w-xl">
                Empresa 100% mexicana. Conectamos tu negocio con DHL, FedEx, UPS y más. Almacenamos, empacamos y enviamos por ti. Todo automatizado.
              </p>
              <div className="flex flex-wrap gap-4 mt-8">
                <Link to="/cotizar" data-testid="hero-cotizar-btn" className="btn-primary flex items-center gap-2 text-lg px-8 py-4">
                  <Calculator size={22} /> Cotizar Envío
                </Link>
                <Link to="/fulfillment" data-testid="hero-ff-btn" className="btn-secondary flex items-center gap-2 text-lg px-8 py-4">
                  <Warehouse size={22} /> Fulfillment
                </Link>
              </div>
              <div className="flex items-center gap-8 mt-10">
                {[
                  { value: "50+", label: "Años" },
                  { value: "100+", label: "Franquicias" },
                  { value: "1M+", label: "Envíos/año" },
                ].map((s, i) => (
                  <div key={i} className="text-center">
                    <span className="text-3xl font-black text-[#5D4037]">{s.value}</span>
                    <p className="text-xs text-stone-500 mt-1">{s.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }} className="relative hidden lg:block">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img src="https://images.unsplash.com/photo-1587293852726-70cdb56c2866?auto=format&w=800&q=80" alt="Almacén Todo de Cartón" className="w-full h-[480px] object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#3E2723]/70 via-[#3E2723]/20 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="inline-flex bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-sm">
                    <img src={LOGOS.tdc_dark} alt="Todo de Cartón" className="h-10 w-auto object-contain" />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CARRIER LOGOS — Real styled badges */}
      <section className="py-6 bg-[#5D4037]">
        <div className="max-w-7xl mx-auto px-4">
          <Marquee speed={35} gradient={false} className="overflow-hidden">
            <div className="flex items-center gap-6 px-4">
              {[...CARRIER_LOGOS, ...CARRIER_LOGOS].map((c, i) => (
                <div key={i} className="flex items-center gap-2 px-5 py-2 rounded-full flex-shrink-0" style={{ backgroundColor: c.bg }}>
                  <span className="font-black text-lg" style={{ color: c.color }}>{c.name}</span>
                </div>
              ))}
            </div>
          </Marquee>
        </div>
      </section>

      {/* SERVICES — 4 Pillars */}
      <section className="section-padding bg-white" data-testid="services-pillars">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="font-mono text-xs font-bold text-[#F57C00] tracking-[0.2em] uppercase">PLATAFORMA INTEGRAL</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#5D4037] mt-3">Todo lo que tu negocio necesita</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Truck, title: "Envíos", desc: "6 paqueterías, comparación instantánea, desde $69 MXN", color: "#F57C00", link: "/cotizar", cta: "Cotizar" },
              { icon: Warehouse, title: "Fulfillment", desc: "Almacenamos, empacamos y enviamos por ti. Todo automatizado", color: "#5D4037", link: "/fulfillment", cta: "Ver más" },
              { icon: Store, title: "Multicanal", desc: "Mercado Libre + Amazon. Inventario y pedidos sincronizados", color: "#2E7D32", link: "/multicanal", cta: "Conectar" },
              { icon: FileText, title: "Archivo Digital", desc: "Digitalización, OCR, nube 24/7. Cumplimiento SAT", color: "#1976D2", link: "/archivo-digital", cta: "Cotizar" },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <Link to={s.link} className="bento-card p-6 flex flex-col h-full group" data-testid={`pillar-${i}`}>
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${s.color}15` }}>
                    <s.icon size={28} style={{ color: s.color }} />
                  </div>
                  <h3 className="text-xl font-bold text-[#5D4037] mb-2">{s.title}</h3>
                  <p className="text-stone-600 text-sm flex-1 mb-4">{s.desc}</p>
                  <span className="text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all" style={{ color: s.color }}>
                    {s.cta} <ArrowRight size={16} />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* GUÍAS PREPAGO — Key conversion section */}
      <section className="section-padding bg-[#5D4037] text-white relative overflow-hidden" data-testid="guides-section">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-3 mb-6">
                <div className="bg-white/95 rounded-xl px-4 py-2 shadow-md">
                  <img src={LOGOS.zipngo} alt="ZipNGo Fulfillment" className="h-8 w-auto object-contain" />
                </div>
              </div>
              <h2 className="text-3xl md:text-4xl font-black leading-tight">Guías Prepago: ahorra hasta <span className="text-[#F57C00]">30%</span> en cada envío</h2>
              <p className="text-white/80 text-lg mt-4">Compra paquetes de 5, 10, 25 o 50 guías. Sin fecha de vencimiento. Genera envíos con cualquier paquetería al instante.</p>
              <div className="grid grid-cols-2 gap-4 mt-8">
                {[
                  { n: "5 guías", p: "$445", d: "" },
                  { n: "10 guías", p: "$790", d: "11% OFF" },
                  { n: "25 guías", p: "$1,725", d: "22% OFF" },
                  { n: "50 guías", p: "$3,100", d: "30% OFF" },
                ].map((pkg, i) => (
                  <div key={i} className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                    <p className="font-bold">{pkg.n}</p>
                    <p className="text-2xl font-black text-[#F57C00]">{pkg.p}</p>
                    {pkg.d && <span className="text-xs bg-[#F57C00] px-2 py-0.5 rounded-full">{pkg.d}</span>}
                  </div>
                ))}
              </div>
              <Link to="/guias-prepago" className="btn-primary mt-8 inline-flex items-center gap-2" data-testid="guides-cta">
                <CreditCard size={20} /> Comprar Guías
              </Link>
            </div>
            <div className="hidden lg:flex justify-center items-center">
              <div className="opacity-10">
                <img src={LOGOS.tdc_horizontal} alt="Todo de Cartón" className="w-60 object-contain brightness-0 invert" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FULFILLMENT HIGHLIGHT */}
      <section className="section-padding bg-[#FAF9F6]" data-testid="fulfillment-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <img src="https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&w=600&q=80" alt="Almacén fulfillment" className="rounded-3xl shadow-xl" loading="lazy" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span className="font-mono text-xs font-bold text-[#5D4037] tracking-[0.2em] uppercase">FULFILLMENT COMPLETO</span>
              <h2 className="text-3xl md:text-4xl font-black text-[#5D4037] mt-3">Tú vendes, nosotros hacemos el resto</h2>
              <p className="text-stone-600 text-lg mt-4">Envía tu inventario a nuestro almacén. Cuando llega un pedido de cualquier canal, nosotros hacemos picking, empaque con materiales premium, y envío con la mejor paquetería. Automáticamente.</p>
              <div className="space-y-3 mt-6">
                {["Almacenamiento en bodega climatizada", "Picking y packing profesional", "Cajas y empaque de calidad incluidos", "Selección automática de mejor paquetería", "Guía y rastreo generados al instante", "API + webhooks para tu sistema"].map((item, i) => (
                  <div key={i} className="flex items-center gap-3"><CheckCircle size={20} className="text-[#2E7D32] flex-shrink-0" /><span className="text-stone-700">{item}</span></div>
                ))}
              </div>
              <Link to="/fulfillment" className="btn-primary mt-8 inline-flex items-center gap-2"><Warehouse size={20} /> Centro de Fulfillment</Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section-padding bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="font-mono text-xs font-bold text-[#F57C00] tracking-[0.2em] uppercase">TESTIMONIOS</span>
            <h2 className="text-3xl md:text-4xl font-black text-[#5D4037] mt-3">Lo que dicen nuestros clientes</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bento-card p-6" data-testid={`testimonial-${i}`}>
                <div className="flex gap-1 mb-4">{[...Array(t.rating)].map((_, j) => <Star key={j} size={16} className="fill-[#F57C00] text-[#F57C00]" />)}</div>
                <p className="text-stone-600 italic mb-6">"{t.text}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-stone-100">
                  <div className="w-10 h-10 rounded-full bg-[#5D4037] text-white flex items-center justify-center font-bold">{t.name[0]}</div>
                  <div>
                    <p className="font-bold text-[#5D4037] text-sm">{t.name}</p>
                    <p className="text-xs text-stone-500">{t.role} · {t.company}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ARCHIVO MUERTO DIGITAL */}
      <section className="section-padding bg-[#FAF9F6]" data-testid="archive-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img src={LOGOS.archivo} alt="Archivo Muerto Digital" className="h-14 w-auto object-contain mb-6" />
              <h2 className="text-3xl md:text-4xl font-black text-[#5D4037] leading-tight">
                ¿Tu archivo muerto ocupa <span className="text-[#2E7D32]">demasiado espacio?</span>
              </h2>
              <p className="text-stone-600 text-lg mt-4">Nosotros digitalizamos, almacenamos y te damos acceso 24/7 a todos tus documentos. Cumple con el SAT y libera metros cuadrados de oficina.</p>
              <div className="grid grid-cols-2 gap-3 mt-6">
                {[
                  { icon: Cloud, title: "Nube 24/7" },
                  { icon: Search, title: "OCR Inteligente" },
                  { icon: Lock, title: "Cumplimiento SAT" },
                  { icon: Shield, title: "Cifrado AES-256" },
                ].map((f, i) => (
                  <div key={i} className="flex items-center gap-2 bg-white rounded-xl p-3 border border-stone-200">
                    <f.icon size={20} className="text-[#2E7D32]" />
                    <span className="text-sm font-medium text-[#5D4037]">{f.title}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-4 mt-8">
                <Link to="/archivo-digital" className="btn-primary flex items-center gap-2"><Calculator size={18} /> Cotizar</Link>
                <Link to="/login" className="btn-secondary flex items-center gap-2"><FolderOpen size={18} /> Mi Archivo</Link>
              </div>
            </div>
            <div className="relative">
              <img src="https://images.pexels.com/photos/4792285/pexels-photo-4792285.jpeg?auto=compress&cs=tinysrgb&w=600" alt="Archivo" className="rounded-3xl shadow-xl" loading="lazy" />
              <div className="absolute -bottom-4 -right-4 bg-[#2E7D32] text-white rounded-2xl p-5 shadow-xl">
                <p className="text-3xl font-black">$45</p>
                <p className="text-sm text-white/80">MXN/caja/mes</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-white" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <span className="font-mono text-xs font-bold text-[#F57C00] tracking-[0.2em] uppercase">PREGUNTAS FRECUENTES</span>
            <h2 className="text-3xl md:text-4xl font-black text-[#5D4037] mt-3">¿Tienes dudas?</h2>
          </div>
          <div className="space-y-3">
            {FAQ_DATA.map((faq, i) => (
              <div key={i} className="bento-card overflow-hidden" data-testid={`faq-${i}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full p-5 flex items-center justify-between text-left">
                  <span className="font-semibold text-[#5D4037] pr-4">{faq.q}</span>
                  <ChevronDown size={20} className={`text-stone-400 flex-shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} className="px-5 pb-5">
                    <p className="text-stone-600">{faq.a}</p>
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FRANCHISE CTA */}
      <section className="section-padding bg-[#2E7D32] relative overflow-hidden" data-testid="franchise-cta">
        <div className="max-w-7xl mx-auto relative z-10 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-white">Únete a nuestra red de 100+ franquicias</h2>
          <p className="text-white/80 text-lg mt-4 max-w-2xl mx-auto">Desde el año 2000 nos constituimos como una exitosa red de franquicias con modelo probado y retorno comprobado.</p>
          <div className="flex justify-center gap-4 mt-8">
            <Link to="/franquicias" className="btn-primary">Más información</Link>
            <Link to="/contacto" className="bg-white text-[#2E7D32] font-semibold px-6 py-3 rounded-full hover:bg-white/90 transition-all">Contactar</Link>
          </div>
        </div>
      </section>

      {/* DEVELOPER API CTA */}
      <section className="section-padding bg-[#FAF9F6]" data-testid="api-cta">
        <div className="max-w-4xl mx-auto text-center">
          <span className="font-mono text-xs font-bold text-[#F57C00] tracking-[0.2em] uppercase">PARA DESARROLLADORES</span>
          <h2 className="text-3xl font-black text-[#5D4037] mt-3">API REST completa para tu sistema</h2>
          <p className="text-stone-600 mt-4">Integra envíos, fulfillment, inventario y más directamente a tu ERP, tienda online o app. Documentación completa y webhooks en tiempo real.</p>
          <Link to="/documentacion-api" className="btn-secondary mt-6 inline-flex items-center gap-2"><BarChart3 size={18} /> Ver Documentación API</Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
